import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Stack,
    TextField,
    Typography,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en-gb";

dayjs.extend(customParseFormat);

import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";

import { LocalizationProvider, TimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getCachevalue } from "../../../Utility/Services/CacheProviderService";
import { AppointmentCard, CommonPatientCard, CommonPatientDetails } from "../../../Components/Common Components/CommonComponents";
import { ConsultantPatientCard } from "../../../Components/Common Components/CommonComponents";
import { ContentInTooltip } from "../../../Components/CommonFunctions/CommonFunctions";



const emptyCombination = () => ({
    id: null,
    drug_id: null,
    drug_name: "",
    quantity: "",
    uom: "",
    is_billable: false,
    drug_uom: "",
    loose_uom: "",
    medicineList: [], // per-combination drug options for Autocomplete
});

const emptyDrug = () => ({
    id: null,
    drug_id: null,
    drug_name: "",
    quantity: "",
    uom: "",
    drug_uom: "",
    loose_uom: "",
    is_billable: false,
    combinations: [],
    medicineList: [], // per-drug options for Autocomplete (like DocPrescription)
});

const emptyTherapy = () => ({
    id: null,
    service_charge_id: null,
    treatment_ms_id: null,
    therapy_name: "",
    duration: "",
    duration_mode: "Mins",
    is_vasthi: false,
    instruction: "",
    drugs: [emptyDrug()],
    therapyList: [], // per-therapy options (same concept as drug.medicineList) - separate list per therapy, no interference
});

/* ------------------ COMPONENT ------------------ */

const TreatmentPlan = ({ from_ip = false }) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [savedPlans, setSavedPlans] = useState([]);
    const [copyPlans, setCopyPlans] = useState([]);
    const [medicineOptions, setMedicineOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [treatmentToDelete, setTreatmentToDelete] = useState(null);
    const [editingSlotRow, setEditingSlotRow] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [expandedSlot, setExpandedSlot] = useState(null);

    let patientData = getCachevalue("IppatientData");
    const patientDetails = patientData ? JSON.parse(patientData) : null;
    const startDateObj = patientDetails?.start_date ? dayjs(patientDetails?.start_date, "DD-MM-YYYY", true) : dayjs();
    // Track deleted IDs for API payload
    const [deletedDrugIds, setDeletedDrugIds] = useState([]);
    const [deletedTreatmentIds, setDeletedTreatmentIds] = useState([]);
    const [sessionIds, setSessionIds] = useState(new Map()); // Track session IDs by slot index
    const [selectedStatus, setSelectedStatus] = useState("All");
    /* ------------------ API ------------------ */

    // Helper function to determine session (Morning/Evening) based on time
    const getSessionFromTime = (time) => {
        if (!time) return "Morning";
        const hour = dayjs(time).hour();
        // Morning: 6 AM to 12 PM, Evening: 12 PM to 6 AM next day
        return hour >= 6 && hour < 12 ? "Morning" : "Evening";
    };

    // Transform sessions structure to flat structure for UI (new GET API structure)
    // const transformSessionsToFlat = (apiData) => {
    //     const flatPlans = [];
    //     if (Array.isArray(apiData)) {
    //         apiData.forEach((dateGroup) => {
    //             const date = dateGroup.date || dateGroup.treatment_date; // Support both formats
    //             if (dateGroup.sessions && Array.isArray(dateGroup.sessions)) {
    //                 dateGroup.sessions.forEach((session) => {
    //                     // Store session ID for later use
    //                     if (session.id) {
    //                         // We'll need to map this to slot index when editing
    //                     }

    //                     // Parse time from "10:30 AM" format to "HH:mm:ss"
    //                     let treatmentTime = session.treatment_time;
    //                     if (treatmentTime && treatmentTime.includes("AM") || treatmentTime.includes("PM")) {
    //                         // Convert "10:30 AM" to "10:30:00"
    //                         treatmentTime = dayjs(treatmentTime, "hh:mm A").format("HH:mm:ss");
    //                     }

    //                     // Parse date from "27-01-2026" to "YYYY-MM-DD"
    //                     let treatmentDate = session.treatment_date || date;
    //                     if (treatmentDate && treatmentDate.includes("-")) {
    //                         // Check if it's DD-MM-YYYY format
    //                         const parts = treatmentDate.split("-");
    //                         if (parts.length === 3 && parts[0].length === 2) {
    //                             treatmentDate = dayjs(treatmentDate, "DD-MM-YYYY").format("YYYY-MM-DD");
    //                         }
    //                     }

    //                     if (session.treatments && Array.isArray(session.treatments)) {
    //                         session.treatments.forEach((treatment) => {
    //                             flatPlans.push({
    //                                 id: treatment.id || null,
    //                                 session_id: session.id || null, // Store session ID
    //                                 treatment_date: treatmentDate,
    //                                 treatment_time: treatmentTime,
    //                                 therapy_name: treatment.treatment_name || "",
    //                                 duration: treatment.duration,
    //                                 duration_mode: treatment.duration_mode || "Minutes",
    //                                 is_vasthi: session.is_vasthi || false,
    //                                 instruction: session.instruction || "",
    //                                 service_charge_id: treatment.service_charge_id,
    //                                 treatment_ms_id: treatment.treatment_ms_id,
    //                                 status: session?.status,
    //                                 drugs: (treatment.drugs || []).map((drug) => ({
    //                                     ...drug,
    //                                     drug_name: drug.drug_name || "",
    //                                     combinations: (drug.combinations || []).map((comb) => ({
    //                                         ...comb,
    //                                         drug_name: comb.drug_name || "",
    //                                     })),
    //                                 })),
    //                             });
    //                         });
    //                     }
    //                 });
    //             }
    //         });
    //     }
    //     return flatPlans;
    // };

    const loadPlans = () => {
        if (!patientDetails) return;
        setLoading(true);
        RestAPIService.getAll(
            `${Serviceurls.TREATMENT_PLAN_GET_POST}?${from_ip ? `ip_admission_id=${patientDetails.id}` : `walkin_admission_id=${patientDetails.id}`}`
        )
            .then((res) => {
                if (res.data.status === "success") {
                    // Transform sessions structure to flat structure for UI
                    setSavedPlans(res.data.data || []);
                    setCopyPlans(res.data.data || []);
                }
            })
            .catch(() => setErrorMsg("Failed to load plans"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (patientDetails?.id) {
            loadPlans();
        }
    }, [patientDetails?.id]);

    // Per-therapy search: update only that therapy's therapyList (same concept as drug.medicineList - separate list, no interference)
    const searchTherapyForCell = useCallback((slotIdx, therapyIdx, key) => {
        if (!key || String(key).trim().length < 3) {
            setSlots((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                if (!therapy) return prev;
                // Keep current selection in list so display doesn't clear (like drug when key < 2)
                if (therapy.service_charge_id && therapy.therapy_name) {
                    therapy.therapyList = [{
                        service_charge_id: therapy.service_charge_id,
                        treatment_ms_id: therapy.treatment_ms_id,
                        treatment_name: therapy.therapy_name,
                        service_name: therapy.therapy_name,
                    }];
                } else {
                    therapy.therapyList = [];
                }
                return next;
            });
            return;
        }
        RestAPIService.getAll(
            `${Serviceurls.AYURVEDA_THERAPY_SEARCH}?treatment_name=${encodeURIComponent(key.trim())}`
        )
            .then((res) => {
                if (res.data.status !== "success") return;
                const searchResults = (res.data.data || []).map((o) => ({
                    service_charge_id: o.service_charge_id,
                    treatment_ms_id: o.treatment_ms_id,
                    treatment_name: o.treatment_name || o.service_name || "",
                    service_name: o.service_name || o.treatment_name || "",
                }));
                setSlots((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                    if (!therapy) return prev;
                    const currentOption = therapy.service_charge_id ? {
                        service_charge_id: therapy.service_charge_id,
                        treatment_ms_id: therapy.treatment_ms_id,
                        treatment_name: therapy.therapy_name,
                        service_name: therapy.therapy_name,
                    } : null;
                    const byId = new Map();
                    if (currentOption) byId.set(currentOption.service_charge_id, currentOption);
                    searchResults.forEach((o) => { if (!byId.has(o.service_charge_id)) byId.set(o.service_charge_id, o); });
                    therapy.therapyList = Array.from(byId.values());
                    return next;
                });
            })
            .catch(() => {
                setSlots((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                    if (!therapy) return prev;
                    if (therapy.service_charge_id && therapy.therapy_name) {
                        therapy.therapyList = [{
                            service_charge_id: therapy.service_charge_id,
                            treatment_ms_id: therapy.treatment_ms_id,
                            treatment_name: therapy.therapy_name,
                            service_name: therapy.therapy_name,
                        }];
                    } else therapy.therapyList = [];
                    return next;
                });
            });
    }, []);

    const therapySearchTimeoutRef = useRef(null);
    const debouncedSearchTherapy = useCallback((slotIdx, therapyIdx, key) => {
        if (therapySearchTimeoutRef.current) clearTimeout(therapySearchTimeoutRef.current);
        therapySearchTimeoutRef.current = setTimeout(() => {
            therapySearchTimeoutRef.current = null;
            searchTherapyForCell(slotIdx, therapyIdx, key);
        }, 300);
    }, [searchTherapyForCell]);

    const fetchTreatmentDetails = (serviceChargeId, slotIdx, therapyIdx) => {
        if (!serviceChargeId) return;
        RestAPIService.getAll(Serviceurls.AYURVEDA_TREATMENT_MEDICINE_GET + `?service_charge_id=${serviceChargeId}`
        )
            .then((res) => {
                if (res.data.status === "success" && res.data.data) {
                    const data = res.data.data;
                    const updatedSlots = [...slots];
                    const therapy = updatedSlots[slotIdx].therapies[therapyIdx];

                    // Map drugs from API - set medicineList so Autocomplete can show drug name without search
                    // If no drugs or empty: keep one empty drug row so user can add manually
                    if (data.drugs && Array.isArray(data.drugs) && data.drugs.length > 0) {
                        therapy.drugs = data.drugs.map((drug) => {
                            const drugId = drug.drug_id;
                            const drugOption = { drug_ms_id: drugId, id: drugId, drug_name: drug.drug_name || "" };
                            return {
                                drug_id: drugId,
                                drug_name: drug.drug_name || "",
                                quantity: drug.quantity || "",
                                uom: drug.uom || drug.drug_uom || "",
                                drug_uom: drug.drug_uom || drug.uom || "",
                                is_billable: drug.is_billable === true,
                                original_quantity: drug.is_billable === false ? (Number(drug.quantity) || 0) : undefined,
                                loose_uom: drug.loose_uom || drug.min_uom || "",
                                medicineList: [drugOption],
                                combinations: (drug.combinations || []).map((comb) => {
                                    const combId = comb.drug_id;
                                    const combOption = { drug_ms_id: combId, id: combId, drug_name: comb.drug_name || "" };
                                    return {
                                        drug_id: combId,
                                        drug_name: comb.drug_name || "",
                                        quantity: comb.quantity || "",
                                        uom: comb.uom || "",
                                        is_billable: comb.is_billable === true,
                                        original_quantity: comb.is_billable === false ? (Number(comb.quantity) || 0) : undefined,
                                        drug_uom: comb.drug_uom || comb.uom || "",
                                        loose_uom: comb.loose_uom || comb.min_uom || "",
                                        medicineList: [combOption],
                                    };
                                }),
                            };
                        });
                    } else {
                        therapy.drugs = [emptyDrug()];
                    }

                    therapy.treatment_ms_id = data.treatment_ms_id;
                    therapy.duration = data.duration || "";
                    therapy.duration_mode = data.duration_mode || "Minutes";
                    therapy.therapyList = therapy.service_charge_id && therapy.therapy_name
                        ? [{ service_charge_id: therapy.service_charge_id, treatment_ms_id: therapy.treatment_ms_id, treatment_name: therapy.therapy_name, service_name: therapy.therapy_name }]
                        : [];

                    setSlots(updatedSlots);
                }
            })
            .catch(() => {
                setErrorMsg("Failed to load treatment details");
            });
    };

    const fetchDrugDetails = (slotIdx, therapyIdx, drugIdx, isCombination = false, combIdx = null, overrideDrugId = null, pharmacyId = null) => {
        const drugId = overrideDrugId ?? (() => {
            const drug = slots[slotIdx]?.therapies?.[therapyIdx]?.drugs?.[drugIdx];
            if (!drug) return null;
            return isCombination ? drug.combinations[combIdx]?.drug_id : drug.drug_id;
        })();
        if (!drugId) return;

        RestAPIService.getAll(
            `${Serviceurls.AYURVEDA_MEDICINE_PREPARE_DRUG_DETAILS}?drug_ms_id=${drugId}&pharmacy_id=${pharmacyId}`
        )
            .then((res) => {
                if (res.data.status !== "success" || !res.data.data?.[0]) return;
                const drugData = res.data.data[0];
                setSlots((prev) => {
                    const next = prev.map((s, si) => ({
                        ...s, therapies: (s.therapies || []).map((th, ti) => ({
                            ...th, drugs: (th.drugs || []).map((dr, di) => {
                                if (si !== slotIdx || ti !== therapyIdx || di !== drugIdx) return dr;
                                if (isCombination && combIdx !== null) {
                                    const comb = (dr.combinations || [])[combIdx];
                                    if (!comb) return dr;
                                    const newComb = { ...comb, drug_uom: drugData.drug_uom || drugData.uom || "", loose_uom: drugData.loose_uom || drugData.min_uom || "", uom: drugData.drug_uom || drugData.uom || comb.uom };
                                    const newCombs = [...(dr.combinations || [])];
                                    newCombs[combIdx] = newComb;
                                    return { ...dr, combinations: newCombs };
                                }
                                return { ...dr, drug_uom: drugData.drug_uom || drugData.uom || "", loose_uom: drugData.loose_uom || drugData.min_uom || "", uom: drugData.drug_uom || drugData.uom || dr.uom };
                            })
                        }))
                    }));
                    return next;
                });
            })
            .catch(() => { });
    };

    const medicineSearchTimeoutRef = useRef(null);

    // Per-drug/comb search: update only that drug's or combination's medicineList (like DocPrescription)
    const searchMedicineForCell = useCallback((slotIdx, therapyIdx, drugIdx, isCombination, combIdx, key) => {
        if (!key || String(key).trim().length < 2) {
            setSlots((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                if (!therapy) return prev;
                if (isCombination && therapy.drugs[drugIdx]?.combinations?.[combIdx]) {
                    therapy.drugs[drugIdx].combinations[combIdx].medicineList = [];
                } else if (therapy.drugs[drugIdx]) {
                    therapy.drugs[drugIdx].medicineList = [];
                }
                return next;
            });
            return;
        }
        RestAPIService.getAll(
            `${Serviceurls.AYURVEDA_MEDICINE_PREPARE_DRUG_SEARCH}?search_key=${encodeURIComponent(key.trim())}`
        )
            .then((res) => {
                if (res.data.status !== "success") return;
                const results = (res.data.data || []).map((item) => ({
                    drug_ms_id: item.drug_ms_id,
                    drug_id: item.drug_id,
                    drug_name: item.drug_name || "",
                    pharmacy_id: item.pharmacy_id || null,
                    // uom: item.uom || "",
                    // drug_uom: item.drug_uom || "",
                    // loose_uom: item.loose_uom || "",
                    // is_billable: item.is_billable === true,
                    // original_quantity: item.is_billable === false ? (Number(item.quantity) || 0) : undefined,
                }));
                setSlots((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                    if (!therapy) return prev;
                    if (isCombination && therapy.drugs[drugIdx]?.combinations?.[combIdx]) {
                        therapy.drugs[drugIdx].combinations[combIdx].medicineList = results;
                    } else if (therapy.drugs[drugIdx]) {
                        therapy.drugs[drugIdx].medicineList = results;
                    }
                    return next;
                });
            })
            .catch(() => {
                setSlots((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    const therapy = next[slotIdx]?.therapies?.[therapyIdx];
                    if (!therapy) return prev;
                    if (isCombination && therapy.drugs[drugIdx]?.combinations?.[combIdx]) {
                        therapy.drugs[drugIdx].combinations[combIdx].medicineList = [];
                    } else if (therapy.drugs[drugIdx]) {
                        therapy.drugs[drugIdx].medicineList = [];
                    }
                    return next;
                });
            });
    }, []);

    const debouncedSearchMedicine = useCallback(
        (slotIdx, therapyIdx, drugIdx, isCombination, combIdx, key) => {
            if (medicineSearchTimeoutRef.current) clearTimeout(medicineSearchTimeoutRef.current);
            medicineSearchTimeoutRef.current = setTimeout(() => {
                medicineSearchTimeoutRef.current = null;
                searchMedicineForCell(slotIdx, therapyIdx, drugIdx, isCombination, combIdx, key);
            }, 300);
        },
        [searchMedicineForCell]
    );

    /* ------------------ VALIDATION ------------------ */

    const isCombinationValid = (comb) =>
        comb.drug_id && Number(comb.quantity) > 0 && comb.uom;

    const isDrugValid = (drug) => {
        if (!drug.drug_id || (Number(drug.quantity) || 0) <= 0 || !drug.uom) return false;
        if (drug.combinations && drug.combinations.length > 0) {
            return drug.combinations.every(isCombinationValid);
        }
        return true;
    };

    const isTherapyValid = (therapy) => {
        const drugsWithId = (therapy.drugs || []).filter((d) => d.drug_id);
        return (
            therapy.service_charge_id &&
            therapy.duration &&
            drugsWithId.length > 0 &&
            drugsWithId.every(isDrugValid)
        );
    };

    const isSlotValid = (slot, timeOptional = false) => {
        const timeOk = timeOptional || slot.slot_time;
        return (
            timeOk &&
            slot.therapies.length > 0 &&
            slot.therapies.every(isTherapyValid) &&
            (!slot.is_vasthi || slot.instruction)
        );
    };

    const getTotalTherapyDurationMinutes = (slot) =>
        (slot.therapies || []).reduce((sum, t) => sum + (Number(t.duration) || 0), 0);

    const canAddTherapy = (slotIdx) => {
        const slot = slots[slotIdx];
        const timeOk = !!editingSlotRow || slot.slot_time;
        return timeOk && slot.therapies.every(isTherapyValid);
    };

    const canSave = () => {
        if (!patientDetails?.id || !selectedDate || slots.length === 0) return false;
        const timeOptional = !!editingSlotRow;
        if (!slots.every((s) => isSlotValid(s, timeOptional))) return false;
        if (editingSlotRow && Number(editingSlotRow.duration) > 0) {
            const total = getTotalTherapyDurationMinutes(slots[0]);
            if (total > Number(editingSlotRow.duration)) return false;
        }
        return true;
    };

    /* ------------------ PAYLOAD ------------------ */

    const buildDrugsPayload = (drugs) => {
        return drugs
            .filter((drug) => drug.drug_id)
            .map((drug) => {
                const isUserAddedDrug = drug.id == null;
                const isBillableDrug = isUserAddedDrug ? true : (drug.is_billable !== false);
                const qty = Number(drug.quantity) || 0;
                const origQty = Number(drug.original_quantity) || 0;
                const billableQty = isBillableDrug ? qty : Math.max(0, qty - origQty);
                return {
                    id: drug.id,
                    drug_id: drug.drug_id,
                    quantity: qty,
                    uom: drug.uom,
                    is_billable: isBillableDrug,
                    billable_qty: billableQty,
                    combinations: (drug.combinations || [])
                        .filter((comb) => comb.drug_id)
                        .map((comb) => {    
                            const isUserAddedComb = comb.id == null;
                            const isBillableComb = isUserAddedComb ? true : (comb.is_billable !== false);
                            const cQty = Number(comb.quantity) || 0;
                            const cOrigQty = Number(comb.original_quantity) || 0;
                            const cBillableQty = isBillableComb ? cQty : Math.max(0, cQty - cOrigQty);
                            return {
                                id: comb.id,
                                drug_id: comb.drug_id,
                                quantity: cQty,
                                uom: comb.uom,
                                is_billable: isBillableComb,
                                billable_qty: cBillableQty,
                            };
                        }),
                };
            });
    };

    /* ------------------ ACTIONS ------------------ */
    const removeSlot = (slotIdx) => {
        if (slots.length > 1) {
            setSlots(slots.filter((_, idx) => idx !== slotIdx));
        }
    };

    const addTherapy = (slotIdx) => {
        if (!canAddTherapy(slotIdx)) {
            setErrorMsg("Please complete all therapies in current slot before adding a new one");
            return;
        }
        const updatedSlots = [...slots];
        updatedSlots[slotIdx].therapies.push(emptyTherapy());
        setSlots(updatedSlots);
    };

    const removeTherapy = (slotIdx, therapyIdx) => {
        const updatedSlots = [...slots];
        if (updatedSlots[slotIdx].therapies.length > 1) {
            const therapyToRemove = updatedSlots[slotIdx].therapies[therapyIdx];
            // Track deleted treatment ID if it exists
            if (therapyToRemove.id) {
                setDeletedTreatmentIds((prev) => [...prev, therapyToRemove.id].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
                // Also track deleted drug IDs from this therapy
                if (therapyToRemove.drugs && therapyToRemove.drugs.length > 0) {
                    const drugIds = therapyToRemove.drugs
                        .map(drug => {
                            const ids = [];
                            if (drug.id) ids.push(drug.id);
                            if (drug.combinations && drug.combinations.length > 0) {
                                drug.combinations.forEach(comb => {
                                    if (comb.id) ids.push(comb.id);
                                });
                            }
                            return ids;
                        })
                        .flat()
                        .filter(id => id);
                    if (drugIds.length > 0) {
                        setDeletedDrugIds((prev) => [...prev, ...drugIds].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
                    }
                }
            }
            updatedSlots[slotIdx].therapies = updatedSlots[slotIdx].therapies.filter(
                (_, idx) => idx !== therapyIdx
            );
            setSlots(updatedSlots);
        }
    };

    const addDrug = (slotIdx, therapyIdx) => {
        const updatedSlots = [...slots];
        updatedSlots[slotIdx].therapies[therapyIdx].drugs.push(emptyDrug());
        setSlots(updatedSlots);
    };

    const addCombination = (slotIdx, therapyIdx, drugIdx) => {
        const updatedSlots = [...slots];
        updatedSlots[slotIdx].therapies[therapyIdx].drugs[drugIdx].combinations.push(
            emptyCombination()
        );
        setSlots(updatedSlots);
    };

    const removeDrug = (slotIdx, therapyIdx, drugIdx) => {
        const updatedSlots = [...slots];
        if (updatedSlots[slotIdx].therapies[therapyIdx].drugs.length > 1) {
            const drugToRemove = updatedSlots[slotIdx].therapies[therapyIdx].drugs[drugIdx];
            // Track deleted drug ID if it exists
            if (drugToRemove.id) {
                setDeletedDrugIds((prev) => [...prev, drugToRemove.id].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
                // Also track deleted combination IDs
                if (drugToRemove.combinations && drugToRemove.combinations.length > 0) {
                    const combIds = drugToRemove.combinations
                        .map(comb => comb.id)
                        .filter(id => id);
                    if (combIds.length > 0) {
                        setDeletedDrugIds((prev) => [...prev, ...combIds].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
                    }
                }
            }
            updatedSlots[slotIdx].therapies[therapyIdx].drugs = updatedSlots[slotIdx].therapies[
                therapyIdx
            ].drugs.filter((_, idx) => idx !== drugIdx);
            setSlots(updatedSlots);
        }
    };

    const removeCombination = (slotIdx, therapyIdx, drugIdx, combIdx) => {
        const updatedSlots = [...slots];
        const combToRemove = updatedSlots[slotIdx].therapies[therapyIdx].drugs[drugIdx].combinations[combIdx];
        // Track deleted combination ID if it exists
        if (combToRemove.id) {
            setDeletedDrugIds((prev) => [...prev, combToRemove.id].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
        }
        updatedSlots[slotIdx].therapies[therapyIdx].drugs[drugIdx].combinations =
            updatedSlots[slotIdx].therapies[therapyIdx].drugs[drugIdx].combinations.filter(
                (_, idx) => idx !== combIdx
            );
        setSlots(updatedSlots);
    };

    const clearFields = () => {
        // Edit dialog only: clear therapy, medicine, vasti, time, instruction — keep date and session (initial stage of update)
        if (editingSlotRow) {
            setSlots([{
                slot_time: null,
                is_vasthi: false,
                instruction: "",
                therapies: [emptyTherapy()],
            }]);
            setDeletedDrugIds([]);
            setDeletedTreatmentIds([]);
            return;
        }
    };

    const clearForm = () => {
        setSelectedDate(null);
        setSlots([]);
        setDeletedDrugIds([]);
        setDeletedTreatmentIds([]);
        setSessionIds(new Map());
    };

    const savePlan = () => {
        if (!patientDetails?.id) {
            setErrorMsg("Admission Id is required to save");
            return;
        }
        if (!canSave()) {
            setErrorMsg("Please fill all required fields");
            return;
        }
        if (editingSlotRow && Number(editingSlotRow.duration) > 0) {
            const totalMins = getTotalTherapyDurationMinutes(slots[0]);
            if (totalMins > Number(editingSlotRow.duration)) {
                setErrorMsg(`Total treatment duration (${totalMins} mins) cannot exceed session duration (${editingSlotRow.duration} mins).`);
                return;
            }
        }

        // Single slot only: build one session from slots[0]; POST sends one session object, not sessions array
        const slot = slots[0];
        const is_vasthi = slot.is_vasthi || false;
        const instruction = slot.instruction || "";
        const sessionId = editingSlotRow ? editingSlotRow.id : (sessionIds.get(0) || null);
        const sessionName = editingSlotRow ? editingSlotRow.session : getSessionFromTime(slot.slot_time);

        const treatments = slot.therapies
            .filter((therapy) => therapy.service_charge_id && therapy.treatment_ms_id)
            .map((therapy) => ({
                id: therapy.id || null,
                service_charge_id: therapy.service_charge_id,
                treatment_ms_id: therapy.treatment_ms_id,
                duration: Number(therapy.duration) || 30,
                duration_mode: therapy.duration_mode || "Minutes",
                drugs: buildDrugsPayload(therapy.drugs || []),
            }));

        if (treatments.length === 0) {
            setErrorMsg("Please add at least one treatment");
            return;
        }

        const sessionPayload = {
            id: sessionId,
            treatment_date: selectedDate.format("YYYY-MM-DD"),
            session: sessionName,
            treatment_time: slot?.slot_time != null ? dayjs(slot.slot_time).format("HH:mm:ss") : null,
            is_vasthi: is_vasthi,
            instruction: instruction,
            treatments: treatments,
        };

        const payload = {
            ...(from_ip ? { ip_admission_id: patientDetails?.id } : { walkin_admission_id: patientDetails?.id }),
            deleted_drug_id: deletedDrugIds,
            deleted_treatment_id: deletedTreatmentIds,
            sessions: sessionPayload,
        };

        setLoading(true);
        setErrorMsg(""); // clear any previous error before request
        RestAPIService.create(payload, Serviceurls.TREATMENT_PLAN_GET_POST)
            .then((res) => {
                const status = res?.data?.status;
                if (status === "success") {
                    setErrorMsg("");
                    setSuccessMsg(res?.data?.message);
                    setEditDialogOpen(false);
                    setEditingSlotRow(null);
                    clearForm();
                    loadPlans(); // refresh table
                } else {
                    setErrorMsg(res?.data?.message || "Save failed");
                }
            })
            .catch((err) => {
                setErrorMsg(err?.response?.data?.message || "Save failed");
            })
            .finally(() => setLoading(false));
    };

    const handleDelete = (treatmentId, allTreatmentIds = null) => {
        // If allTreatmentIds is provided, delete all treatments in the session
        // Track deleted treatment IDs for API payload
        if (allTreatmentIds && allTreatmentIds.length > 0) {
            setDeletedTreatmentIds((prev) => [...prev, ...allTreatmentIds.filter(id => id && !prev.includes(id))]);
        } else if (treatmentId) {
            setDeletedTreatmentIds((prev) => [...prev, treatmentId].filter((id, idx, arr) => id && arr.indexOf(id) === idx));
        }
        setTreatmentToDelete({ id: treatmentId, allIds: allTreatmentIds });
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!treatmentToDelete) return;
        setLoading(true);

        // Handle both old format (just ID) and new format (object with id and allIds)
        const treatmentId = typeof treatmentToDelete === 'object' ? treatmentToDelete.id : treatmentToDelete;
        const allIds = typeof treatmentToDelete === 'object' && treatmentToDelete.allIds
            ? treatmentToDelete.allIds
            : [treatmentId];

        // Delete all treatments in the session if multiple IDs provided
        const deletePromises = allIds.map(id =>
            RestAPIService.delete(`${Serviceurls.TREATMENT_PLAN_GET_POST}?treatment_plan_id=${id}`)
        );

        Promise.all(deletePromises)
            .then((results) => {
                if (results.every(res => res.data.status === "success")) {
                    setSuccessMsg("Treatment Plan Deleted");
                    loadPlans();
                } else {
                    setErrorMsg("Some deletions failed");
                }
            })
            .catch(() => setErrorMsg("Delete failed"))
            .finally(() => {
                setLoading(false);
                setDeleteDialogOpen(false);
                setTreatmentToDelete(null);
            });
    };

    const handleEdit = (plan) => {
        if (!plan) return;
        const slotTime = plan.treatment_time
            ? (String(plan.treatment_time).includes("AM") || String(plan.treatment_time).includes("PM")
                ? dayjs(plan.treatment_time, "hh:mm A")
                : dayjs(plan.treatment_time, "HH:mm:ss"))
            : null;
        const therapies = (plan.treatments || []).length > 0
            ? (plan.treatments || []).map((t) => {
                const therapyList = (t.service_charge_id && t.treatment_name)
                    ? [{ service_charge_id: t.service_charge_id, treatment_ms_id: t.treatment_ms_id, treatment_name: t.treatment_name, service_name: t.treatment_name }]
                    : [];
                const drugs = (t.drugs || []).map((d) => {
                    const drugOpt = d.drug_id && d.drug_name ? { drug_ms_id: d.drug_id, id: d.drug_id, drug_name: d.drug_name } : null;
                    const combinations = (d.combinations || []).map((c) => ({
                        id: c.id || null,
                        drug_id: c.drug_id,
                        drug_name: c.drug_name || "",
                        quantity: String(c.quantity ?? ""),
                        uom: c.uom || "",
                        drug_uom: (c.drug_uom || c.uom || ""),
                        loose_uom: (c.loose_uom || c.uom || ""),
                        is_billable: c.is_billable !== false,
                        original_quantity: c.is_billable === false ? (Number(c.quantity) || 0) : undefined,
                        medicineList: c.drug_id && c.drug_name ? [{ drug_ms_id: c.drug_id, id: c.drug_id, drug_name: c.drug_name }] : [],
                    }));
                    return {
                        id: d.id || null,
                        drug_id: d.drug_id,
                        drug_name: d.drug_name || "",
                        quantity: String(d.quantity ?? ""),
                        uom: d.uom || "",
                        drug_uom: d.drug_uom || d.uom || "",
                        loose_uom: d.loose_uom || d.uom || "",
                        is_billable: d.is_billable !== false,
                        original_quantity: d.is_billable === false ? (Number(d.quantity) || 0) : undefined,
                        combinations,
                        medicineList: drugOpt ? [drugOpt] : [],
                    };
                });
                return {
                    id: t.id || null,
                    service_charge_id: t.service_charge_id,
                    treatment_ms_id: t.treatment_ms_id,
                    therapy_name: t.treatment_name || "",
                    duration: Number(t.duration ?? ""),
                    duration_mode: t.duration_mode || "Minutes",
                    drugs: drugs.length > 0 ? drugs : [emptyDrug()],
                    therapyList,
                };
            })
            : [emptyTherapy()];
        setSlots([{
            slot_time: slotTime,
            is_vasthi: plan.is_vasthi || false,
            instruction: plan.instruction || "",
            therapies,
        }]);
        setSelectedDate(dayjs(plan.treatment_date, "DD-MM-YYYY"));
        setSessionIds(new Map([[0, plan.id]]));
        setEditingSlotRow({ ...plan });
        setEditDialogOpen(true);
        setDeletedDrugIds([]);
        setDeletedTreatmentIds([]);
    };

    const closeEditDialog = () => {
        setEditDialogOpen(false);
        setEditingSlotRow(null);
        setSlots([]);
        setSelectedDate(null);
        setDeletedDrugIds([]);
        setDeletedTreatmentIds([]);
        setSessionIds(new Map());
    };

    /* ------------------ UI HELPERS ------------------ */
    const getStatusBadge = (status) => {
        const statusConfig = {
            Completed: { color: "#43C64F", bgColor: "#ECFFE9" },
            "Assign Treatment": { color: "#F1584A", bgColor: "#FFE9E9" },
            Scheduled: { color: "#29B6F6", bgColor: "#E3F2FD" },
            "Not Scheduled": { color: "#FF7723", bgColor: "#FFF3E0" },
            Cancelled: { color: "#9E9E9E", bgColor: "#F5F5F5" },
        };

        const config = statusConfig[status] || statusConfig["Not Scheduled"];
        return (
            <Chip
                label={status}
                size="small"
                sx={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                }}
            />
        );
    };

    const groupPlansByDate = () => {
        const grouped = {};
        savedPlans.forEach((plan) => {
            // Normalize date format for grouping (convert DD-MM-YYYY to YYYY-MM-DD for consistency)
            let date = plan.treatment_date;
            if (date && date.includes("-")) {
                const parts = date.split("-");
                if (parts.length === 3 && parts[0].length === 2) {
                    // DD-MM-YYYY format
                    date = dayjs(date, "DD-MM-YYYY").format("YYYY-MM-DD");
                }
            }
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(plan);
        });
        return grouped;
    };

    const groupByTime = (plans) => {
        const grouped = {};
        plans.forEach((plan) => {
            const time = plan.treatment_time;
            if (!grouped[time]) {
                grouped[time] = [];
            }
            grouped[time].push(plan);
        });
        return grouped;
    };

    const isDateDisabled = (date) => {
        if (!date) return false;
        const dateObj = dayjs(date);
        return dateObj.isBefore(startDateObj, "day");
    };

    const getTherapyDaysCount = () => {
        if (!savedPlans.length) return 0;
        // Normalize dates to YYYY-MM-DD format for proper counting
        const uniqueDates = new Set(
            savedPlans.map((p) => {
                let date = p.treatment_date;
                if (date && date.includes("-")) {
                    const parts = date.split("-");
                    if (parts.length === 3 && parts[0].length === 2) {
                        // DD-MM-YYYY format
                        date = dayjs(date, "DD-MM-YYYY").format("YYYY-MM-DD");
                    }
                }
                return date;
            })
        );
        return uniqueDates.size;
    };

    const therapyDaysCount = getTherapyDaysCount();
    const therapyDaysRemaining = (patientDetails?.no_of_days) - therapyDaysCount;

    const getUomOptions = (drug) => {
        const options = [];
        if (drug.drug_uom) options.push(drug.drug_uom);
        if (drug.loose_uom && drug.loose_uom !== drug.drug_uom) options.push(drug.loose_uom);
        return options;
    };

    let attender = {
        lineOne: "AttenderName",
        lineTwo: `${patientDetails?.attender_name ? patientDetails?.attender_name : '-'}`,
        lineFour: 'ContactNumber',
        LineFive: `${patientDetails?.attender_mobile ? patientDetails?.attender_mobile : '-'}`
    }
    let consultant = {
        lineOne: "ConsultantName",
        lineTwo: `${patientDetails?.doctor_name ? patientDetails?.doctor_name : '-'}`,
    }
    const onCardClick = (status) => {
        setSelectedStatus(status);
        if (status === "All") {
            setSavedPlans(copyPlans);
        } else {
            const filteredSlots = copyPlans.filter(plan => plan.status === status);
            setSavedPlans(filteredSlots);
        }
    }

    return (
        <Box p={.5} bgcolor={Colors.Background} display="flex" flexDirection="column" height="calc(100vh - 108px)" sx={{ minHeight: 0 }}>
            <Box display={'flex'} alignItems='center' emed_tid="surgery_notes_header_content">
                <Box className='eMed_Patient_Details_Long eMed_patient_box' emed_tid="surgery_notes_patient_details">
                    <CommonPatientDetails data={patientDetails} showDetailed={true} emed_tid="surgery_notes_patient_details_component" />
                </Box>
                <CommonPatientCard details={attender} showDetailed={true} emed_tid="surgery_notes_attender_card" />
                <ConsultantPatientCard name={consultant} emed_tid="surgery_notes_consultant_card" />
            </Box>
            <Box display={"flex"} justifyContent={"space-between"} alignItems="center" flexShrink={0}>
                <Typography fontWeight={500} fontSize="1rem">
                </Typography>
                <Box>
                    <Box display="flex" gap={1}>
                        <AppointmentCard onClick={() => onCardClick("All")} count={copyPlans.length || 0} label={"All"} color={"#04B7B1"} isSelectedCard={selectedStatus === "All"} />
                        <AppointmentCard onClick={() => onCardClick("Assign Treatment")} count={copyPlans.filter(plan => plan.status === "Assign Treatment").length || 0} label={"Assign Treatment"} color={"#616161"} isSelectedCard={selectedStatus === "Assign Treatment"} />
                        <AppointmentCard onClick={() => onCardClick("Not Scheduled")} count={copyPlans.filter(plan => plan.status === "Not Scheduled").length || 0} label={"Not Scheduled"} color={"orange"} isSelectedCard={selectedStatus === "Not Scheduled"} />
                        <AppointmentCard onClick={() => onCardClick("Scheduled")} count={copyPlans.filter(plan => plan.status === "Scheduled").length || 0} label={"Scheduled"} color={"#0461B7"} isSelectedCard={selectedStatus === "Scheduled"} />
                        <AppointmentCard onClick={() => onCardClick("Completed")} count={copyPlans.filter(plan => plan.status === "Completed").length || 0} label={"Completed"} color={"green"} isSelectedCard={selectedStatus === "Completed"} />
                        <AppointmentCard onClick={() => onCardClick("Cancelled")} count={copyPlans.filter(plan => plan.status === "Cancelled").length || 0} label={"Cancelled"} color={"red"} isSelectedCard={selectedStatus === "Cancelled"} />
                    </Box>
                </Box>
            </Box>

            {/* Edit dialog - only way to add/edit treatment; date disabled, time optional; no Add Slot */}
            <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="lg" fullWidth scroll="paper">
                <DialogTitle>
                    Edit Session — {editingSlotRow ? dayjs(editingSlotRow.treatment_date, "DD-MM-YYYY").format("DD-MM-YYYY") : ""} {editingSlotRow?.session || ""}
                </DialogTitle>
                <DialogContent>
                    <Box bgcolor="#fff" p={1} sx={{ display: "flex", flexDirection: "column" }}>
                        <TableContainer sx={{ overflow: 'auto', height: '60dvh' }}>
                            <Table size="small" sx={{ "& .MuiTableCell-root": { p: 0.5 } }}>
                                <TableHead sx={{backgroundColor: Colors.TableHeaderBarBGColor }}>
                                    <TableRow sx={{ backgroundColor: Colors.TableHeaderBarBGColor }} stickyHeader>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 120, backgroundColor: Colors.TableHeaderBarBGColor }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 120, backgroundColor: Colors.TableHeaderBarBGColor }}>Time (optional)</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 200, backgroundColor: Colors.TableHeaderBarBGColor }}>Treatment Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 100, backgroundColor: Colors.TableHeaderBarBGColor }}>Duration</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 80, backgroundColor: Colors.TableHeaderBarBGColor }}>Vasthi</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 200, backgroundColor: Colors.TableHeaderBarBGColor }}>Instruction</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 80, backgroundColor: Colors.TableHeaderBarBGColor }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {slots?.length > 0 && slots.map((slot, slotIdx) => {
                                        const showDateForSlot = slotIdx === 0;
                                        return (
                                            <React.Fragment key={slotIdx}>
                                                {slot.therapies.map((therapy, therapyIdx) => {
                                                    const isFirstTherapy = therapyIdx === 0;
                                                    const showTimeForTherapy = isFirstTherapy;
                                                    const showVasthiForTherapy = isFirstTherapy;
                                                    const showInstructionForTherapy = isFirstTherapy;

                                                    return (
                                                        <React.Fragment key={`${slotIdx}-therapy-${therapyIdx}`}>
                                                            <TableRow>
                                                                <TableCell>
                                                                    {showDateForSlot && isFirstTherapy && (
                                                                        <TextField
                                                                            size="small"
                                                                            value={selectedDate ? dayjs(selectedDate).format("DD-MM-YYYY") : ""}
                                                                            disabled
                                                                            sx={{ width: 160 }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {showTimeForTherapy && (
                                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                            <TimePicker
                                                                                value={slot.slot_time}
                                                                                onChange={(newTime) => {
                                                                                    const updated = [...slots];
                                                                                    updated[slotIdx].slot_time = newTime;
                                                                                    setSlots(updated);
                                                                                }}
                                                                                ampm={true}
                                                                                renderInput={(params) => (
                                                                                    <TextField {...params} size="small" sx={{ width: 150 }} placeholder="Optional" />
                                                                                )}
                                                                            />
                                                                        </LocalizationProvider>
                                                                    )}
                                                                </TableCell>
                                                                {/* Treatment Name */}
                                                                <TableCell>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Autocomplete
                                                                            size="small"
                                                                            options={therapy.therapyList || []}
                                                                            getOptionLabel={(o) => o?.service_name || o?.treatment_name || ""}
                                                                            isOptionEqualToValue={(option, value) => (option?.service_charge_id && value?.service_charge_id) ? option.service_charge_id === value.service_charge_id : option === value}
                                                                            onInputChange={(e, v) => debouncedSearchTherapy(slotIdx, therapyIdx, v)}
                                                                            onChange={(e, v) => {
                                                                                const scId = v?.service_charge_id;
                                                                                if (scId) {
                                                                                    const sameSlotTherapies = slots[slotIdx]?.therapies || [];
                                                                                    const duplicate = sameSlotTherapies.some(
                                                                                        (t, idx) => idx !== therapyIdx && t.service_charge_id === scId
                                                                                    );
                                                                                    if (duplicate) {
                                                                                        setErrorMsg("Same therapy cannot be added twice in the same slot. Add it in another slot.");
                                                                                        return;
                                                                                    }
                                                                                }
                                                                                const updated = [...slots];
                                                                                const th = updated[slotIdx].therapies[therapyIdx];
                                                                                th.service_charge_id = scId ?? null;
                                                                                th.therapy_name = v?.treatment_name || v?.service_name || "";
                                                                                th.treatment_ms_id = v?.treatment_ms_id ?? null;
                                                                                th.therapyList = v ? [v] : [];
                                                                                setSlots(updated);
                                                                                if (scId) {
                                                                                    fetchTreatmentDetails(
                                                                                        scId,
                                                                                        slotIdx,
                                                                                        therapyIdx
                                                                                    );
                                                                                }
                                                                            }}
                                                                            value={
                                                                                (therapy.therapyList || []).find((o) => o?.service_charge_id === therapy.service_charge_id) || null
                                                                            }
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search Therapy"
                                                                                    size="small"
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        startAdornment: (
                                                                                            <InputAdornment position="start">
                                                                                                <SearchIcon fontSize="small" />
                                                                                            </InputAdornment>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            sx={{ width: 180, flex: 1 }}
                                                                        />
                                                                        {slot.therapies.length > 1 && (
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => removeTherapy(slotIdx, therapyIdx)}
                                                                                sx={{ width: 28, height: 28 }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </Stack>
                                                                </TableCell>
                                                                {/* Duration */}
                                                                <TableCell>
                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                        <TextField
                                                                            size="small"
                                                                            value={therapy.duration}
                                                                            onChange={(e) => {
                                                                                const updated = [...slots];
                                                                                updated[slotIdx].therapies[therapyIdx].duration =
                                                                                    e.target.value;
                                                                                setSlots(updated);
                                                                            }}
                                                                            disabled={true}
                                                                            sx={{ width: 60 }}
                                                                        />
                                                                        <FormControl size="small" sx={{ width: 120 }}>
                                                                            <Select
                                                                                value={therapy.duration_mode}
                                                                                onChange={(e) => {
                                                                                    const updated = [...slots];
                                                                                    updated[slotIdx].therapies[
                                                                                        therapyIdx
                                                                                    ].duration_mode = e.target.value;
                                                                                    setSlots(updated);
                                                                                }}
                                                                            >
                                                                                <MenuItem value="Minutes">Minutes</MenuItem>
                                                                                {/* <MenuItem value="Hours">Hours</MenuItem> */}
                                                                            </Select>
                                                                        </FormControl>
                                                                    </Stack>
                                                                </TableCell>
                                                                {/* Vasthi */}
                                                                <TableCell>
                                                                    {showVasthiForTherapy && (
                                                                        <Checkbox
                                                                            checked={slot.is_vasthi || false}
                                                                            onChange={(e) => {
                                                                                const updated = [...slots];
                                                                                updated[slotIdx].is_vasthi = e.target.checked;
                                                                                setSlots(updated);
                                                                            }}
                                                                            size="small"
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                {/* Instruction */}
                                                                <TableCell>
                                                                    {showInstructionForTherapy && (
                                                                        <TextField
                                                                            size="small"
                                                                            value={slot.instruction || ""}
                                                                            onChange={(e) => {
                                                                                const updated = [...slots];
                                                                                updated[slotIdx].instruction = e.target.value;
                                                                                setSlots(updated);
                                                                            }}
                                                                            inputProps={{ maxlength: 250 }}
                                                                            placeholder="Instruction"
                                                                            fullWidth
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                {/* Action */}
                                                                <TableCell>
                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: "#4da3d4",
                                                                                "&:hover": { backgroundColor: "#3a8bb3" },
                                                                                minWidth: "30px",
                                                                                width: "30px",
                                                                                height: "30px",
                                                                                p: 0,
                                                                            }}
                                                                            onClick={() => addTherapy(slotIdx)}
                                                                            disabled={!canAddTherapy(slotIdx)}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                        {slot.therapies.length > 1 && (
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => removeTherapy(slotIdx, therapyIdx)}
                                                                                sx={{ width: 28, height: 28 }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </Stack>
                                                                </TableCell>
                                                            </TableRow>

                                                            {/* Medicine Rows: one treatment = multiple drugs; each drug = main row + combination rows. Color is per-treatment per-drug (drugIdx resets for each therapy). */}
                                                            {therapy.drugs.map((drug, drugIdx) => {
                                                                const rowBg = drugIdx % 2 === 0 ? "#fafafa" : "#f0f8ff";
                                                                const borderColor = drugIdx % 2 === 0 ? "gray" : "#4da3d4";
                                                                return (
                                                                    <React.Fragment key={drugIdx}>
                                                                        {/* Main Drug Row */}
                                                                        <TableRow sx={{ backgroundColor: rowBg }}>
                                                                            <TableCell></TableCell>
                                                                            <TableCell></TableCell>
                                                                            <TableCell>
                                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                                    <Button
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        sx={{
                                                                                            borderColor: borderColor,
                                                                                            color: borderColor,
                                                                                            backgroundColor: rowBg,
                                                                                            minWidth: "30px",
                                                                                            width: "30px",
                                                                                            height: "30px",
                                                                                            p: 0,
                                                                                        }}
                                                                                        onClick={() => addCombination(slotIdx, therapyIdx, drugIdx)}
                                                                                    >
                                                                                        +
                                                                                    </Button>
                                                                                    <Autocomplete
                                                                                        size="small"
                                                                                        options={drug.medicineList || []}
                                                                                        getOptionLabel={(o) => o?.drug_name || ""}
                                                                                        onInputChange={(e, v) => {
                                                                                            debouncedSearchMedicine(slotIdx, therapyIdx, drugIdx, false, null, v);
                                                                                        }}
                                                                                        onChange={(e, v) => {
                                                                                            const id = v?.drug_ms_id ?? v?.id;
                                                                                            if (id) {
                                                                                                const therapyDrugs = slots[slotIdx]?.therapies?.[therapyIdx]?.drugs || [];
                                                                                                const duplicate = therapyDrugs.some(
                                                                                                    (d, idx) => idx !== drugIdx && (d.drug_id === id || d.drug_id === String(id))
                                                                                                );
                                                                                                if (duplicate) {
                                                                                                    setErrorMsg("This drug is already selected in this therapy. Select a different drug.");
                                                                                                    return;
                                                                                                }
                                                                                            }
                                                                                            const updated = [...slots];
                                                                                            const currentDrug =
                                                                                                updated[slotIdx].therapies[therapyIdx]
                                                                                                    .drugs[drugIdx];
                                                                                            currentDrug.drug_id = id ?? null;
                                                                                            currentDrug.drug_name = v?.drug_name ?? "";
                                                                                            currentDrug.medicineList = v ? [v] : [];
                                                                                            setSlots(updated);
                                                                                            if (id) {
                                                                                                fetchDrugDetails(slotIdx, therapyIdx, drugIdx, false, null, id, v.pharmacy_id);
                                                                                            }
                                                                                        }}
                                                                                        value={
                                                                                            (drug.medicineList || []).find(
                                                                                                (o) => (o.drug_ms_id ?? o.id) === drug.drug_id
                                                                                            ) || null
                                                                                        }
                                                                                        renderInput={(params) => (
                                                                                            <TextField
                                                                                                {...params}
                                                                                                placeholder="Select Medicine Name"
                                                                                                size="small"
                                                                                            />
                                                                                        )}
                                                                                        sx={{ width: 180, flex: 1 }}
                                                                                    />
                                                                                </Stack>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                                    <TextField
                                                                                        size="small"
                                                                                        value={drug.quantity}
                                                                                        onChange={(e) => {
                                                                                            const updated = [...slots];
                                                                                            updated[slotIdx].therapies[therapyIdx].drugs[
                                                                                                drugIdx
                                                                                            ].quantity = e.target.value;
                                                                                            setSlots(updated);
                                                                                        }}
                                                                                        sx={{ width: 60 }}
                                                                                    />
                                                                                    <FormControl size="small" sx={{ width: 60 }}>
                                                                                        <Select
                                                                                            value={drug.uom || ""}
                                                                                            onChange={(e) => {
                                                                                                const updated = [...slots];
                                                                                                updated[slotIdx].therapies[therapyIdx].drugs[
                                                                                                    drugIdx
                                                                                                ].uom = e.target.value;
                                                                                                setSlots(updated);
                                                                                            }}
                                                                                            displayEmpty
                                                                                        >
                                                                                            {getUomOptions(drug).map((uom) => (
                                                                                                <MenuItem key={uom} value={uom}>
                                                                                                    {uom}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                    {therapy.drugs.length > 1 && (
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            color="error"
                                                                                            onClick={() =>
                                                                                                removeDrug(slotIdx, therapyIdx, drugIdx)
                                                                                            }
                                                                                            sx={{ width: 28, height: 28 }}
                                                                                        >
                                                                                            <DeleteIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    )}
                                                                                    {drugIdx === therapy.drugs.length - 1 && (
                                                                                        <Button
                                                                                            variant="contained"
                                                                                            size="small"
                                                                                            sx={{
                                                                                                backgroundColor: "#4da3d4",
                                                                                                "&:hover": { backgroundColor: "#3a8bb3" },
                                                                                                minWidth: "30px",
                                                                                                width: "30px",
                                                                                                height: "30px",
                                                                                                p: 0,
                                                                                            }}
                                                                                            onClick={() => addDrug(slotIdx, therapyIdx)}
                                                                                        >
                                                                                            +
                                                                                        </Button>
                                                                                    )}
                                                                                </Stack>
                                                                            </TableCell>
                                                                            <TableCell></TableCell>
                                                                            <TableCell></TableCell>
                                                                            <TableCell></TableCell>
                                                                        </TableRow>

                                                                        {/* Combination Drug Rows (same color as parent drug - only for this treatment's drug) */}
                                                                        {(drug.combinations || []).map((comb, combIdx) => (
                                                                            <TableRow key={combIdx} sx={{ backgroundColor: rowBg }}>
                                                                                <TableCell></TableCell>
                                                                                <TableCell></TableCell>
                                                                                <TableCell sx={{ borderLeft: `5px solid ${borderColor}`, pl: 1.5 }}>
                                                                                    <Box>
                                                                                        <Autocomplete
                                                                                            size="small"
                                                                                            options={comb.medicineList || []}
                                                                                            getOptionLabel={(o) => o?.drug_name || ""}
                                                                                            onInputChange={(e, v) => {
                                                                                                debouncedSearchMedicine(slotIdx, therapyIdx, drugIdx, true, combIdx, v);
                                                                                            }}
                                                                                            onChange={(e, v) => {
                                                                                                const id = v?.drug_ms_id ?? v?.id;
                                                                                                if (id) {
                                                                                                    const sameDrugCombs = slots[slotIdx]?.therapies?.[therapyIdx]?.drugs?.[drugIdx]?.combinations || [];
                                                                                                    const duplicate = sameDrugCombs.some(
                                                                                                        (c, idx) => idx !== combIdx && (c.drug_id === id || c.drug_id === String(id))
                                                                                                    );
                                                                                                    if (duplicate) {
                                                                                                        setErrorMsg("This drug is already selected in this combination. Select a different drug.");
                                                                                                        return;
                                                                                                    }
                                                                                                }
                                                                                                const updated = [...slots];
                                                                                                const currentComb =
                                                                                                    updated[slotIdx].therapies[therapyIdx]
                                                                                                        .drugs[drugIdx].combinations[combIdx];
                                                                                                currentComb.drug_id = id ?? null;
                                                                                                currentComb.drug_name = v?.drug_name ?? "";
                                                                                                currentComb.medicineList = v ? [v] : [];
                                                                                                setSlots(updated);
                                                                                                if (id) {
                                                                                                    fetchDrugDetails(slotIdx, therapyIdx, drugIdx, true, combIdx, id, v.pharmacy_id);
                                                                                                }
                                                                                            }}
                                                                                            value={
                                                                                                (comb.medicineList || []).find(
                                                                                                    (o) => (o.drug_ms_id ?? o.id) === comb.drug_id
                                                                                                ) || null
                                                                                            }
                                                                                            renderInput={(params) => (
                                                                                                <TextField
                                                                                                    {...params}
                                                                                                    placeholder="Select Medicine Name"
                                                                                                    size="small"
                                                                                                />
                                                                                            )}
                                                                                            sx={{ width: "100%" }}
                                                                                        />
                                                                                    </Box>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                                        <TextField
                                                                                            size="small"
                                                                                            value={comb.quantity}
                                                                                            onChange={(e) => {
                                                                                                const updated = [...slots];
                                                                                                updated[slotIdx].therapies[therapyIdx].drugs[
                                                                                                    drugIdx
                                                                                                ].combinations[combIdx].quantity =
                                                                                                    e.target.value;
                                                                                                setSlots(updated);
                                                                                            }}
                                                                                            sx={{ width: 60 }}
                                                                                        />
                                                                                        <FormControl size="small" sx={{ width: 60 }}>
                                                                                            <Select
                                                                                                value={comb.uom || ""}
                                                                                                onChange={(e) => {
                                                                                                    const updated = [...slots];
                                                                                                    updated[slotIdx].therapies[therapyIdx].drugs[
                                                                                                        drugIdx
                                                                                                    ].combinations[combIdx].uom = e.target.value;
                                                                                                    setSlots(updated);
                                                                                                }}
                                                                                                displayEmpty
                                                                                            >
                                                                                                {getUomOptions(comb).map((uom) => (
                                                                                                    <MenuItem key={uom} value={uom}>
                                                                                                        {uom}
                                                                                                    </MenuItem>
                                                                                                ))}
                                                                                            </Select>
                                                                                        </FormControl>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            color="error"
                                                                                            onClick={() =>
                                                                                                removeCombination(
                                                                                                    slotIdx,
                                                                                                    therapyIdx,
                                                                                                    drugIdx,
                                                                                                    combIdx
                                                                                                )
                                                                                            }
                                                                                            sx={{ width: 28, height: 28 }}
                                                                                        >
                                                                                            <DeleteIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Stack>
                                                                                </TableCell>
                                                                                <TableCell></TableCell>
                                                                                <TableCell></TableCell>
                                                                                <TableCell></TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                flexShrink: 0,
                                position: "sticky",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: "#fff",
                                borderTop: "1px solid #e0e0e0",
                                px: 2,
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                zIndex: 10,
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={closeEditDialog}
                                sx={{
                                    borderColor: "#CDD1D5",
                                    color: "#2F2A53",
                                    "&:hover": { borderColor: "#B3D9FE" },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={clearFields}
                                sx={{
                                    borderColor: "#CDD1D5",
                                    color: "#2F2A53",
                                    "&:hover": { borderColor: "#B3D9FE" },
                                }}
                            >
                                CLEAR
                            </Button>
                            <Button
                                variant="contained"
                                disabled={!canSave()}
                                onClick={savePlan}
                                sx={{
                                    backgroundColor: "#4da3d4",
                                    "&:hover": { backgroundColor: "#3a8bb3" },
                                    "&:disabled": { backgroundColor: "#EAEAEA", color: "#A9A9A9" },
                                }}
                            >
                                SAVE
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* TABLE - Accordion View with sticky header */}
            <Box bgcolor="#fff" borderRadius={1} sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 0.5, display: "flex", flexDirection: "column", mt: 1 }}>
                {savedPlans.length === 0 ? (
                    <Typography color="textSecondary" align="center" py={4}>
                        No treatment plans found
                    </Typography>
                ) : (
                    <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                        <Table
                            className="accordion-tab-plan"
                            stickyHeader
                            sx={{
                                "& .MuiTableCell-root": {
                                    p: 0.5
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow sx={{ backgroundColor: Colors.Primary, color: "white" }}>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 100, color: "white", backgroundColor: Colors.Primary }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 100, color: "white", backgroundColor: Colors.Primary }}>Session</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 100, color: "white", backgroundColor: Colors.Primary }}>Time</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 200, color: "white", backgroundColor: Colors.Primary }}>Treatment Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 100, color: "white", backgroundColor: Colors.Primary }}>Duration</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 80, color: "white", backgroundColor: Colors.Primary }}>Vasthi</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 180, color: "white", backgroundColor: Colors.Primary }}>Instruction</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 120, color: "white", backgroundColor: Colors.Primary }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 120, color: "white", backgroundColor: Colors.Primary }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {savedPlans.map((plan) => {
                                    const treatments = plan.treatments || [];
                                    const hasTreatments = treatments.length > 0;
                                    const totalDuration = treatments.reduce((acc, t) => acc + (Number(t.duration) || 0), 0);
                                    const firstTreatment = treatments[0];
                                    const isExpanded = expandedSlot === plan.id;
                                    const formatTime = (t) => {
                                        if (!t) return "-";
                                        if (typeof t === "string" && (t.includes("AM") || t.includes("PM"))) return dayjs(t, "hh:mm A").format("hh:mm A");
                                        return dayjs(t, "HH:mm:ss").format("hh:mm A");
                                    };
                                    const drugsSummary = (trts) => {
                                        const list = [];
                                        (trts || []).forEach((t) => {
                                            (t.drugs || []).forEach((d) => {
                                                list.push(`${d.drug_name || "-"} ${Number(d.quantity) || 0} ${d.uom || ""}`);
                                                (d.combinations || []).forEach((c) => list.push(`${c.drug_name || "-"} ${Number(c.quantity) || 0} ${c.uom || ""}`));
                                            });
                                        });
                                        return list.length ? list.join("; ") : "-";
                                    };
                                    return (
                                        <React.Fragment key={plan.id}>
                                            <TableRow
                                                sx={{
                                                    "&:hover": { backgroundColor: Colors.HoverColor },
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => setExpandedSlot(isExpanded ? null : plan.id)}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {plan.treatment_date ? dayjs(plan.treatment_date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("DD-MM-YYYY") : "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{plan.session || "-"}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{formatTime(plan.treatment_time)}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {hasTreatments ? treatments.map((t) => t.treatment_name || "-").join(", ") : "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {hasTreatments ? `${totalDuration} ${firstTreatment?.duration_mode || "Minutes"}` : "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2">{plan.is_vasthi ? "Yes" : "No"}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{ContentInTooltip(plan.instruction || "-", 25, "0.85rem")}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(plan.status || "Not Scheduled")}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <IconButton
                                                            size="small"
                                                            disabled={plan.status === "Completed" || plan.status === "Cancelled"}
                                                            onClick={() => handleEdit(plan)}
                                                            sx={{
                                                                backgroundColor: "#E3F2FD",
                                                                color: "#2196F3",
                                                                "&:hover": { backgroundColor: "#BBDEFB" },
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={plan.status === "Completed" || plan.status === "Cancelled"}
                                                            onClick={() => handleDelete(plan.id)}
                                                            sx={{
                                                                backgroundColor: "#FFEBEE",
                                                                color: "#F44336",
                                                                "&:hover": { backgroundColor: "#FFCDD2" },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                        {hasTreatments && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => { e.stopPropagation(); setExpandedSlot(isExpanded ? null : plan.id); }}
                                                                sx={{ border: "1px solid #e0e0e0", "&:hover": { backgroundColor: "#f5f5f5" } }}
                                                            >
                                                                <ExpandMoreIcon sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            {/* Expanded: all treatments and drugs - per treatment: main drug + combinations, alternating color per drug within that treatment */}
                                            {isExpanded && hasTreatments && treatments.map((treatment, tIdx) => (
                                                <React.Fragment key={treatment.id || tIdx || treatment.treatment_name}>
                                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                                        <TableCell colSpan={3}></TableCell>
                                                        <TableCell><Typography variant="body2" fontWeight={600}>{treatment.treatment_name || "-"}</Typography></TableCell>
                                                        <TableCell><Typography variant="body2">{treatment.duration} {treatment.duration_mode || "Minutes"}</Typography></TableCell>
                                                        <TableCell colSpan={4}></TableCell>
                                                    </TableRow>
                                                    {(treatment.drugs || []).map((drug, drugIdx) => {
                                                        const rowBg = drugIdx % 2 === 0 ? "#fafafa" : "#f0f8ff";
                                                        const borderColor = drugIdx % 2 === 0 ? "gray" : "#4da3d4";
                                                        return (
                                                            <React.Fragment key={drug.id || drugIdx}>
                                                                <TableRow sx={{ backgroundColor: rowBg }}>
                                                                    <TableCell colSpan={3}></TableCell>
                                                                    <TableCell sx={{ borderLeft: `5px solid ${borderColor}`, pl: 1.5 }}>
                                                                        <Typography variant="body2">{drug.drug_name || "-"}</Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2">{drug.quantity != null ? Number(drug.quantity) : "-"} {drug.uom || ""}</Typography>
                                                                    </TableCell>
                                                                    <TableCell colSpan={4}></TableCell>
                                                                </TableRow>
                                                                {(drug.combinations || []).map((comb, cIdx) => (
                                                                    <TableRow key={comb.id || cIdx} sx={{ backgroundColor: rowBg }}>
                                                                        <TableCell colSpan={3}></TableCell>
                                                                        <TableCell sx={{ borderLeft: `5px solid ${borderColor}`, pl: 2 }}>
                                                                            <Typography variant="body2">{comb.drug_name || "-"}</Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">{comb.quantity != null ? Number(comb.quantity) : "-"} {comb.uom || ""}</Typography>
                                                                        </TableCell>
                                                                        <TableCell colSpan={4}></TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this treatment plan?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && <Loader />}
            {errorMsg && (
                <ToastMsg severity="error" msg={errorMsg} msgPop={() => setErrorMsg("")} />
            )}
            {successMsg && (
                <ToastMsg
                    severity="success"
                    msg={successMsg}
                    msgPop={() => setSuccessMsg("")}
                />
            )}
        </Box>
    );
};

export default TreatmentPlan;
